import React, { useState, useMemo } from 'react';
import { invoiceGroupsService } from '../services/api';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import CascaDeModal from './shared/CascaDeModal';
import { campo } from '../design';

interface ItemRow {
  name: string;
  quantity: number;
  unitValue: number;
}

interface Props {
  clients: Array<{ id: number; name: string; companyName?: string; cnpj?: string }>;
  onClose: () => void;
  onCreated: (groupId: number) => void;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function NewInvoiceGroupModal({ clients, onClose, onCreated }: Props) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<ItemRow[]>([{ name: '', quantity: 1, unitValue: 0 }]);
  const [installments, setInstallments] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState(todayStr());
  const [intervalMode, setIntervalMode] = useState<'monthly' | 'days'>('monthly');
  const [intervalMonths, setIntervalMonths] = useState(1);
  const [intervalDays, setIntervalDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalValue = useMemo(() => {
    return items.reduce((sum, it) => sum + Number(it.quantity || 0) * Number(it.unitValue || 0), 0);
  }, [items]);

  const installmentValue = useMemo(() => {
    if (installments <= 0) return 0;
    return Math.floor((totalValue * 100) / installments) / 100;
  }, [totalValue, installments]);

  const previewDates = useMemo(() => {
    const out: string[] = [];
    if (!firstDueDate || installments <= 0) return out;
    const [y, m, d] = firstDueDate.split('-').map(Number);
    const baseDay = d;
    for (let i = 0; i < installments; i++) {
      let dt: Date;
      if (intervalMode === 'monthly') {
        const targetYear = y;
        const targetMonth = m - 1 + intervalMonths * i;
        const tentative = new Date(targetYear, targetMonth, 1);
        const lastDay = new Date(tentative.getFullYear(), tentative.getMonth() + 1, 0).getDate();
        dt = new Date(tentative.getFullYear(), tentative.getMonth(), Math.min(baseDay, lastDay));
      } else {
        dt = new Date(y, m - 1, baseDay);
        dt.setDate(dt.getDate() + intervalDays * i);
      }
      out.push(`${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`);
    }
    return out;
  }, [firstDueDate, installments, intervalMode, intervalMonths, intervalDays]);

  const updateItem = (idx: number, patch: Partial<ItemRow>) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addItem = () => setItems(prev => [...prev, { name: '', quantity: 1, unitValue: 0 }]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError(null);
    if (!clientId) return setError('Selecione o cliente');
    if (!description.trim()) return setError('Informe a descrição do serviço');
    if (items.length === 0 || items.some(it => !it.name.trim() || it.unitValue <= 0))
      return setError('Cada item precisa ter nome e valor unitário > 0');
    if (totalValue <= 0) return setError('Valor total deve ser maior que zero');
    if (installments < 1) return setError('Número de parcelas inválido');

    try {
      setSubmitting(true);
      const payload: any = {
        clientId: Number(clientId),
        description: description.trim(),
        items: items.map(it => ({
          name: it.name.trim(),
          quantity: Number(it.quantity),
          unitValue: Number(it.unitValue),
          totalValue: Number((Number(it.quantity) * Number(it.unitValue)).toFixed(2)),
        })),
        totalValue: Number(totalValue.toFixed(2)),
        installments,
        firstDueDate,
        notes: notes.trim() || undefined,
      };
      if (intervalMode === 'monthly') {
        payload.intervalMonths = intervalMonths;
      } else {
        payload.intervalDays = intervalDays;
      }
      const created = await invoiceGroupsService.create(payload);
      onCreated(created.id);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao criar grupo');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Formulário digitado: não fecha ao clicar fora (como já era).
    <CascaDeModal
      largura="lg"
      // O nome é o do guia (§4: "grupo de parcelas", de invoice_groups) e o mesmo do item de menu que abre
      // este modal. Até 06/10/2026 o menu dizia "Novo grupo de parcelas" e o modal "Novo Grupo de Faturamento".
      titulo="Novo grupo de parcelas"
      subtitulo="Cria N faturas (1 boleto cada) + 1 NFS-e única pelo total — para serviços únicos parcelados."
      onClose={onClose}
      corpoClassName="p-6 space-y-5"
      rodape={
        <div className="ml-auto flex gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded disabled:opacity-50"
          >Cancelar</button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded font-medium disabled:opacity-50"
          >{submitting ? 'Criando...' : `Criar Grupo + ${installments} Faturas`}</button>
        </div>
      }
    >
          {error && (
            <div className={`p-3 border rounded text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Cliente *</label>
              <select
                value={clientId}
                onChange={e => setClientId(e.target.value ? Number(e.target.value) : '')}
                className={`w-full px-3 py-2 ${campo} rounded`}
              >
                <option value="">Selecione...</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.cnpj ? ` — ${c.cnpj}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Descrição (vai na NFS-e) *</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Implantação Sala Técnica TI - Central Regulação SAMU 192"
                className={`w-full px-3 py-2 ${campo} rounded`}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-slate-300">Itens do serviço</label>
              <button
                type="button"
                onClick={addItem}
                className="text-xs px-2 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded"
              >+ Adicionar item</button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    value={it.name}
                    onChange={e => updateItem(idx, { name: e.target.value })}
                    placeholder="Nome do item/serviço"
                    className={`col-span-6 px-3 py-2 ${campo} rounded text-sm`}
                  />
                  <input
                    type="number"
                    value={it.quantity}
                    onChange={e => updateItem(idx, { quantity: Number(e.target.value) })}
                    step="0.01"
                    min="0"
                    placeholder="Qtd"
                    className={`col-span-1 px-2 py-2 ${campo} rounded text-sm`}
                  />
                  <input
                    type="number"
                    value={it.unitValue}
                    onChange={e => updateItem(idx, { unitValue: Number(e.target.value) })}
                    step="0.01"
                    min="0"
                    placeholder="Vlr Unit"
                    className={`col-span-2 px-2 py-2 ${campo} rounded text-sm`}
                  />
                  <div className="col-span-2 text-right text-slate-300 text-sm">
                    R$ {fmt(Number(it.quantity || 0) * Number(it.unitValue || 0))}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    className="col-span-1 text-red-400 hover:text-red-300 disabled:opacity-30 text-xl"
                  >×</button>
                </div>
              ))}
            </div>
            <div className="mt-2 text-right text-white font-semibold">
              Valor Total: <span className="text-orange-400">R$ {fmt(totalValue)}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Parcelas *</label>
              <input
                type="number"
                value={installments}
                onChange={e => setInstallments(Math.max(1, Number(e.target.value)))}
                min="1"
                max="60"
                className={`w-full px-3 py-2 ${campo} rounded`}
              />
              <div className="text-xs text-slate-400 mt-1">≈ R$ {fmt(installmentValue)} / parcela</div>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">1ª Vencimento *</label>
              <input
                type="date"
                value={firstDueDate}
                onChange={e => setFirstDueDate(e.target.value)}
                className={`w-full px-3 py-2 ${campo} rounded`}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Intervalo</label>
              <div className="flex gap-1">
                <select
                  value={intervalMode}
                  onChange={e => setIntervalMode(e.target.value as any)}
                  className={`flex-1 px-2 py-2 ${campo} rounded text-sm`}
                >
                  <option value="monthly">Mensal</option>
                  <option value="days">Em dias</option>
                </select>
                {intervalMode === 'monthly' ? (
                  <input
                    type="number"
                    value={intervalMonths}
                    onChange={e => setIntervalMonths(Math.max(1, Number(e.target.value)))}
                    min="1"
                    title="Meses entre parcelas"
                    className={`w-16 px-2 py-2 ${campo} rounded text-sm`}
                  />
                ) : (
                  <input
                    type="number"
                    value={intervalDays}
                    onChange={e => setIntervalDays(Math.max(1, Number(e.target.value)))}
                    min="1"
                    title="Dias entre parcelas"
                    className={`w-16 px-2 py-2 ${campo} rounded text-sm`}
                  />
                )}
              </div>
            </div>
          </div>

          {previewDates.length > 0 && (
            <div className="p-3 bg-central-fundo/50 border border-central-borda rounded">
              <div className="text-xs text-slate-400 mb-2">Vencimentos previstos:</div>
              <div className="flex flex-wrap gap-2">
                {previewDates.map((d, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-700 text-slate-200 rounded text-xs">
                    {i + 1}/{installments}: {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm text-slate-300 mb-1">Observações (opcional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 ${campo} rounded text-sm`}
            />
          </div>
    </CascaDeModal>
  );
}
